import React, { Component } from "react";
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import moment from "moment";
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableSortLabel,
  Button,
  TablePagination,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Chip,
  IconButton,
  FormControlLabel,
  Checkbox,
  Popover,
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";

import { userService } from "_services/user.service";
import enMsg from "__helpers/locale/en/en";

import { apiPath } from "api";
import { generalAuthTokenHeader } from "__helpers/auth-header";

import { connect } from "react-redux";
import { reduxLoad } from "js/actions/index";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import {
  PER_PAGE_OPTIONS,
  DEFAULT_PER_PAGE_RECORDS,
} from "__helpers/constants";
import { basePath, baseRoutes } from "base-routes";

// Format a JS Date as YYYY-MM-DD in the user's LOCAL timezone.
// Avoid moment here: BundlePatient calls moment.tz.setDefault("UTC") at module
// load (admin-routes imports it eagerly), so any moment(date).format() in this
// SPA reads in UTC and shifts the picked day backward for east-of-UTC users.
const toLocalYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

function mapDispatchToProps(dispatch) {
  return {
    reduxLoad: (reduxLoadVal) => dispatch(reduxLoad(reduxLoadVal)),
  };
}
const mapStateToProps = (state) => {
  return {
    userInfoRedux: state.userInfo,
    timestampRedux: state.timestamp,
    reduxLoadFlag: state.reduxLoadFlag,
  };
};

const headCells = [
  { id: "providerName", numeric: false, sorting: true, label: "Provider Name", width: 140 },
  { id: "accountStatus", numeric: false, sorting: true, label: "Acct Status", width: 80 },
  { id: "registered", numeric: false, sorting: true, label: "Reg.", width: 40, hiddenByDefault: true },
  { id: "email", numeric: false, sorting: false, label: "Email", width: 80, hiddenByDefault: true },
  { id: "phone", numeric: false, sorting: false, label: "Phone", width: 70, hiddenByDefault: true },
  { id: "linkedFacilities", numeric: false, sorting: false, label: "Facilities", width: 120 },
  { id: "lastLoginDate", numeric: false, sorting: true, label: "Last Login", width: 75 },
  { id: "lastLoginBy", numeric: false, sorting: false, label: "Login By", width: 80, hiddenByDefault: true },
  { id: "daysSinceLastLogin", numeric: true, sorting: true, label: "Idle", width: 40 },
  { id: "totalReferrals", numeric: true, sorting: true, label: "Total Ref.", width: 45 },
  { id: "acknowledged", numeric: true, sorting: true, label: "Viewed", width: 45 },
  { id: "canAccept", numeric: true, sorting: true, label: "Accept", width: 50 },
  { id: "finalized", numeric: true, sorting: true, label: "Final", width: 45 },
  { id: "declined", numeric: true, sorting: true, label: "Decl.", width: 45 },
  { id: "dropped", numeric: true, sorting: true, label: "Drop", width: 45 },
  { id: "pendingAwaiting", numeric: true, sorting: true, label: "Pend.", width: 45 },
  { id: "avgResponseTimeHours", numeric: true, sorting: true, label: "Avg Resp", width: 55 },
  { id: "responseRate", numeric: true, sorting: true, label: "Rate %", width: 45 },
  { id: "responseRateTrend", numeric: false, sorting: true, label: "Trend", width: 65 },
  { id: "engagementStatus", numeric: false, sorting: true, label: "Status", width: 100 },
];

const HIDDEN_BY_DEFAULT = headCells
  .filter((h) => h.hiddenByDefault)
  .map((h) => h.id);

const TOGGLEABLE_COLUMNS = headCells
  .filter((h) => h.id !== "providerName")
  .map((h) => ({ id: h.id, label: h.label }));

const ACCOUNT_STATUSES = [
  "New",
  "Inactive",
  "Non-Charge",
  "Charge-Paid",
  "Charge-Unpaid",
];
const ENGAGEMENT_STATUSES = [
  "Active",
  "Low Response",
  "Non-Responsive",
  "Never Logged In",
];

const engagementColors = {
  Active: { backgroundColor: "#4caf50", color: "#fff" },
  "Low Response": { backgroundColor: "#ff9800", color: "#fff" },
  "Non-Responsive": { backgroundColor: "#f44336", color: "#fff" },
  "Never Logged In": { backgroundColor: "#9e9e9e", color: "#fff" },
};

const trendColors = {
  Improving: "#4caf50",
  Declining: "#f44336",
  Stable: "#757575",
  New: "#757575",
};

// Scoped styles using #id selector for highest specificity to override global CSS.
const scopedTableStyles = `
  #engagement-report-table .MuiTable-root th {
    min-width: auto !important;
    text-align: center !important;
    padding: 6px 6px !important;
    white-space: nowrap;
  }
  #engagement-report-table .MuiTable-root td {
    text-align: center !important;
    padding: 6px 6px !important;
    white-space: nowrap;
  }
  #engagement-report-table .MuiTable-root th:last-child,
  #engagement-report-table .MuiTable-root td:last-child {
    text-align: center !important;
    width: auto !important;
    min-width: auto !important;
  }
  #engagement-report-table .MuiTable-root th:nth-last-child(2),
  #engagement-report-table .MuiTable-root td:nth-last-child(2) {
    min-width: auto !important;
  }
  #engagement-report-table .MuiTable-root th:last-child > span {
    display: inline-flex !important;
  }
  #engagement-report-table .MuiTable-root th svg {
    margin-left: 2px !important;
  }
  #engagement-report-table .MuiTableSortLabel-root {
    display: inline-flex;
    justify-content: center;
    width: 100%;
  }
`;

class ProviderEngagementReportClass extends Component {
  constructor(props) {
    super(props);
    this.dateUtility = new DateFnsUtils();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.state = {
      loading: false,
      startDate: yesterday,
      endDate: today,
      startDateValid: true,
      endDateValid: true,
      startMaxDate: new Date(),
      facilityList: [],
      facilities: [],
      accountStatusFilter: ["New", "Non-Charge", "Charge-Paid", "Charge-Unpaid"],
      engagementStatusFilter: [],
      reportData: null,
      displayData: [],
      orderBy: "providerName",
      direction: true,
      perPage: DEFAULT_PER_PAGE_RECORDS,
      currentPage: 0,
      hiddenColumns: [...HIDDEN_BY_DEFAULT],
      columnPopoverAnchor: null,
    };
  }

  componentDidMount() {
    fetch(apiPath.getActiveFacilities, {
      method: "GET",
      headers: generalAuthTokenHeader(),
    })
      .then((response) => response.json())
      .then((data) => {
        this.setState({ facilities: data }, () => {
          this.handleGenerateReport();
        });
      })
      .catch((error) => {
        console.error("Error fetching facilities:", error);
        this.handleGenerateReport();
      });
  }

  handleStartDateChange = (date) => {
    const { endDate } = this.state;
    this.setState({ startDate: date, startDateValid: true });
    if (endDate && date > endDate) {
      this.setState({ endDate: null, endDateValid: false });
    }
  };

  handleEndDateChange = (date) => {
    this.setState({ endDate: date, endDateValid: true });
  };

  handleChangeFilter = (event) => {
    const { name, value } = event.target;
    let newValue = Array.isArray(value) ? value.filter((v) => v !== "") : value;
    this.setState({ [name]: newValue }, () => {
      if (this.state.reportData) {
        this.applyClientFilters();
      }
    });
  };

  handleChangeFacilityList = (event) => {
    let value = event.target.value;
    value = value.filter((v) => v !== "");
    this.setState({ facilityList: value }, () => {
      if (this.state.reportData) {
        this.applyClientFilters();
      }
    });
  };

  applyClientFilters = () => {
    if (!this.state.reportData || !this.state.reportData.providers) return;

    let filtered = [...this.state.reportData.providers];

    if (this.state.accountStatusFilter.length > 0) {
      filtered = filtered.filter(
        (p) => this.state.accountStatusFilter.includes(p.accountStatus)
      );
    }
    if (this.state.engagementStatusFilter.length > 0) {
      filtered = filtered.filter(
        (p) => this.state.engagementStatusFilter.includes(p.engagementStatus)
      );
    }
    if (this.state.facilityList.length > 0) {
      const selectedFacilityNames = this.state.facilityList
        .map((id) => {
          const facility = this.state.facilities.find((f) => f.facilityId === id);
          return facility ? facility.facility_name : "";
        })
        .filter(Boolean);
      filtered = filtered.filter(
        (p) =>
          p.linkedFacilities &&
          selectedFacilityNames.some((name) => p.linkedFacilities.includes(name))
      );
    }

    const sorted = this.clientSideSort(
      filtered,
      this.state.orderBy,
      this.state.direction
    );
    this.setState({ displayData: sorted, currentPage: 0 });
  };

  clientSideSort = (data, orderBy, ascending) => {
    return [...data].sort((a, b) => {
      let valA = a[orderBy];
      let valB = b[orderBy];
      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;
      if (typeof valA === "boolean") {
        valA = valA ? 1 : 0;
        valB = valB ? 1 : 0;
      }
      if (typeof valA === "string") {
        return ascending
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
      return ascending ? valA - valB : valB - valA;
    });
  };

  tableSortHandler = (property) => () => {
    this.setState(
      (prevState) => ({
        orderBy: property,
        direction: prevState.orderBy === property ? !prevState.direction : true,
      }),
      () => {
        const sorted = this.clientSideSort(
          this.state.displayData,
          this.state.orderBy,
          this.state.direction
        );
        this.setState({ displayData: sorted });
      }
    );
  };

  handleChangePage = (event, newPage) => {
    this.setState({ currentPage: newPage });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({
      perPage: parseInt(event.target.value),
      currentPage: 0,
    });
  };

  handleProviderClick = (providerId) => {
    window.open(`${basePath}${baseRoutes.providerUser.path}?id=${providerId}`, '_blank');
  };

  handleToggleColumn = (columnId) => {
    this.setState((prevState) => {
      const hidden = prevState.hiddenColumns.includes(columnId)
        ? prevState.hiddenColumns.filter((c) => c !== columnId)
        : [...prevState.hiddenColumns, columnId];
      return { hiddenColumns: hidden };
    });
  };

  handleOpenColumnPopover = (event) => {
    this.setState({ columnPopoverAnchor: event.currentTarget });
  };

  handleCloseColumnPopover = () => {
    this.setState({ columnPopoverAnchor: null });
  };

  isColumnVisible = (columnId) => {
    return !this.state.hiddenColumns.includes(columnId);
  };

  handleGenerateReport = async () => {
    const { startDate, endDate, facilityList } = this.state;

    if (!startDate || !endDate) {
      let showNotification = {
        title: "Provider Engagement Report",
        message: "Please select both start and end dates.",
        type: "danger",
      };
      userService.showNotification(showNotification);
      return;
    }

    this.setState({ loading: true });
    let showNotification = {
      title: "Provider Engagement Report",
      message: "Generating report, please wait...",
      type: "info",
    };
    userService.showNotification(showNotification);

    try {
      const queryParams = [];
      if (startDate) {
        queryParams.push(`startDate=${toLocalYMD(startDate)}`);
      }
      if (endDate) {
        queryParams.push(`endDate=${toLocalYMD(endDate)}`);
      }
      const url = `${apiPath.providerEngagementReport}?${queryParams.join("&")}`;

      const response = await fetch(url, {
        method: "GET",
        headers: generalAuthTokenHeader(),
      });

      if (!response.ok) {
        throw new Error("Failed to generate report");
      }

      const data = await response.json();

      this.setState(
        {
          reportData: data,
          loading: false,
        },
        () => {
          this.applyClientFilters();
          showNotification = {
            title: "Provider Engagement Report",
            message: `Report generated with ${data.totalProviders} providers.`,
            type: "success",
          };
          userService.showNotification(showNotification);
        }
      );
    } catch (error) {
      console.error("Error generating report:", error);
      this.setState({ loading: false });
      showNotification = {
        title: "Provider Engagement Report",
        message: "Failed to generate report. Please try again.",
        type: "danger",
      };
      userService.showNotification(showNotification);
    }
  };

  handleDownloadExcel = async () => {
    const { startDate, endDate, facilityList } = this.state;

    let showNotification = {
      title: "Provider Engagement Report",
      message: "Downloading Excel, please wait...",
      type: "info",
    };
    userService.showNotification(showNotification);

    try {
      const queryParams = [];
      if (startDate) {
        queryParams.push(`startDate=${toLocalYMD(startDate)}`);
      }
      if (endDate) {
        queryParams.push(`endDate=${toLocalYMD(endDate)}`);
      }
      const url = `${apiPath.providerEngagementReportDownload}?${queryParams.join("&")}`;
      await userService.fetchsReferralReportData(url);
    } catch (error) {
      console.error("Error downloading report:", error);
      showNotification = {
        title: "Provider Engagement Report",
        message: "Failed to download Excel report.",
        type: "danger",
      };
      userService.showNotification(showNotification);
    }
  };

  renderSummaryCards() {
    const { reportData } = this.state;
    if (!reportData) return null;

    const cards = [
      {
        label: "Total Providers",
        value: reportData.totalProviders || 0,
        color: "info",
      },
      {
        label: "Active Providers",
        value: reportData.activeProviders || 0,
        color: "success",
      },
      {
        label: "Non-Responsive",
        value: reportData.nonResponsiveProviders || 0,
        color: "danger",
      },
      {
        label: "Never Logged In",
        value: reportData.neverLoggedInProviders || 0,
        color: "warning",
      },
      {
        label: "Avg Response Rate",
        value:
          reportData.avgResponseRate != null
            ? reportData.avgResponseRate.toFixed(1) + "%"
            : "0%",
        color: "info",
      },
    ];

    return (
      <Box display="flex" flexWrap="wrap" style={{ marginBottom: 16, gap: 16 }}>
        {cards.map((card, idx) => (
          <Card
            key={idx}
            style={{
              flex: "1 1 180px",
              minWidth: 180,
              margin: 0,
            }}
          >
            <CardHeader color={card.color} stats>
              <p style={{ fontSize: 14, margin: 0, color: "#fff" }}>
                {card.label}
              </p>
              <h3
                style={{
                  fontSize: 28,
                  fontWeight: "bold",
                  margin: "8px 0 0",
                  color: "#fff",
                }}
              >
                {card.value}
              </h3>
            </CardHeader>
          </Card>
        ))}
      </Box>
    );
  }

  render() {
    const {
      startDate,
      endDate,
      startMaxDate,
      facilities,
      facilityList,
      accountStatusFilter,
      engagementStatusFilter,
      displayData,
      orderBy,
      direction,
      perPage,
      currentPage,
      reportData,
      loading,
      hiddenColumns,
      columnPopoverAnchor,
    } = this.state;

    const visibleHeadCells = headCells.filter(
      (h) => !hiddenColumns.includes(h.id)
    );
    const columnPopoverOpen = Boolean(columnPopoverAnchor);

    const paginatedData = displayData.slice(
      currentPage * perPage,
      currentPage * perPage + perPage
    );

    return (
      <div>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            {this.renderSummaryCards()}

            <Card>
              <CardHeader color="success" className="cusCardTitle">
                <h4 className="cusCardTitleText">
                  Provider Engagement Report
                </h4>
              </CardHeader>
              <CardBody>
                {/* Filters Row */}
                <Box
                  display="flex"
                  flexWrap="wrap"
                  alignItems="flex-end"
                  style={{ gap: 16, marginBottom: 16 }}
                >
                  <Box style={{ minWidth: 160 }}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <KeyboardDatePicker
                        label="Start Date *"
                        format="MM/dd/yyyy"
                        margin="normal"
                        id="start-date-picker"
                        name="startDate"
                        value={startDate}
                        maxDate={startMaxDate}
                        onChange={this.handleStartDateChange}
                        KeyboardButtonProps={{
                          "aria-label": "change date",
                          className: "date-picker-span",
                        }}
                      />
                    </MuiPickersUtilsProvider>
                  </Box>

                  <Box style={{ minWidth: 160 }}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <KeyboardDatePicker
                        label="End Date *"
                        format="MM/dd/yyyy"
                        margin="normal"
                        id="end-date-picker"
                        value={endDate}
                        maxDate={startMaxDate}
                        minDate={startDate}
                        onChange={this.handleEndDateChange}
                        KeyboardButtonProps={{
                          "aria-label": "change date",
                          className: "date-picker-span",
                        }}
                      />
                    </MuiPickersUtilsProvider>
                  </Box>

                  <Box style={{ minWidth: 200 }}>
                    <InputLabel htmlFor="facility-select">
                      Facilities
                    </InputLabel>
                    <Select
                      onChange={this.handleChangeFacilityList}
                      inputProps={{ name: "facilityList" }}
                      value={facilityList.length ? facilityList : [""]}
                      multiple
                      style={{ minWidth: 200 }}
                      MenuProps={{
                        PaperProps: {
                          style: { maxHeight: 48 * 4.5 + 8, width: 250 },
                        },
                        variant: "menu",
                        getContentAnchorEl: null,
                      }}
                    >
                      <MenuItem value="">All Facilities</MenuItem>
                      {facilities.length ? (
                        facilities.map((facility) => (
                          <MenuItem
                            key={facility.facilityId}
                            value={facility.facilityId}
                          >
                            {facility.facility_name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value=" ">Not Available</MenuItem>
                      )}
                    </Select>
                  </Box>

                  <Box style={{ minWidth: 160 }}>
                    <InputLabel htmlFor="account-status-select">
                      Account Status
                    </InputLabel>
                    <Select
                      name="accountStatusFilter"
                      value={accountStatusFilter}
                      onChange={this.handleChangeFilter}
                      style={{ minWidth: 160 }}
                      multiple
                      displayEmpty
                      renderValue={(selected) =>
                        selected.length === 0 ? "All" : selected.join(", ")
                      }
                      MenuProps={{
                        PaperProps: { style: { maxHeight: 48 * 4.5 + 8, width: 250 } },
                        variant: "menu",
                        getContentAnchorEl: null,
                      }}
                    >
                      {ACCOUNT_STATUSES.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  <Box style={{ minWidth: 160 }}>
                    <InputLabel htmlFor="engagement-status-select">
                      Engagement Status
                    </InputLabel>
                    <Select
                      name="engagementStatusFilter"
                      value={engagementStatusFilter}
                      onChange={this.handleChangeFilter}
                      style={{ minWidth: 160 }}
                      multiple
                      displayEmpty
                      renderValue={(selected) =>
                        selected.length === 0 ? "All" : selected.join(", ")
                      }
                      MenuProps={{
                        PaperProps: { style: { maxHeight: 48 * 4.5 + 8, width: 250 } },
                        variant: "menu",
                        getContentAnchorEl: null,
                      }}
                    >
                      {ENGAGEMENT_STATUSES.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>

                {/* Action Buttons */}
                <Box display="flex" alignItems="center" style={{ marginBottom: 16, gap: 10 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={this.handleGenerateReport}
                    disabled={!startDate || !endDate || loading}
                  >
                    {loading ? "Generating..." : "Generate Report"}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={this.handleDownloadExcel}
                    disabled={!reportData}
                    style={{ backgroundColor: "#4caf50", color: "#fff" }}
                  >
                    Download Excel
                  </Button>
                  <IconButton
                    size="small"
                    onClick={this.handleOpenColumnPopover}
                    title="Show/Hide Columns"
                    style={{ marginLeft: 4 }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  <Popover
                    open={columnPopoverOpen}
                    anchorEl={columnPopoverAnchor}
                    onClose={this.handleCloseColumnPopover}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  >
                    <Box style={{ padding: 12, minWidth: 180 }}>
                      <div style={{ fontWeight: "bold", marginBottom: 8, fontSize: 13 }}>Show/Hide Columns</div>
                      {TOGGLEABLE_COLUMNS.map((col) => (
                        <FormControlLabel
                          key={col.id}
                          control={
                            <Checkbox
                              size="small"
                              checked={this.isColumnVisible(col.id)}
                              onChange={() => this.handleToggleColumn(col.id)}
                            />
                          }
                          label={<span style={{ fontSize: 13 }}>{col.label}</span>}
                          style={{ display: "block", margin: 0 }}
                        />
                      ))}
                    </Box>
                  </Popover>
                </Box>

                {/* Data Table */}
                <style dangerouslySetInnerHTML={{ __html: scopedTableStyles }} />
                <div id="engagement-report-table" style={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {visibleHeadCells.map((headCell) => (
                          <TableCell
                            key={headCell.id}
                            align="center"
                            style={{
                              fontWeight: "bold",
                              fontSize: 12,
                              minWidth: headCell.width,
                              whiteSpace: "nowrap",
                              ...(headCell.id === "providerName" && {
                                position: "sticky",
                                left: 0,
                                zIndex: 3,
                                backgroundColor: "#fff",
                                boxShadow: "2px 0 4px -2px rgba(0,0,0,0.15)",
                              }),
                            }}
                          >
                            {headCell.sorting ? (
                              <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={direction ? "asc" : "desc"}
                                onClick={this.tableSortHandler(headCell.id)}
                              >
                                {headCell.label}
                              </TableSortLabel>
                            ) : (
                              headCell.label
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedData.length ? (
                        paginatedData.map((row) => (
                          <TableRow key={row.providerId}>
                            {this.isColumnVisible("providerName") && (
                            <TableCell
                              style={{
                                position: "sticky",
                                left: 0,
                                zIndex: 1,
                                backgroundColor: "#fff",
                                boxShadow: "2px 0 4px -2px rgba(0,0,0,0.15)",
                                fontSize: 13,
                              }}
                            >
                              <span
                                style={{ color: "#1976d2", cursor: "pointer", textDecoration: "underline" }}
                                onClick={() => this.handleProviderClick(row.providerId)}
                              >
                                {row.providerName}
                              </span>
                            </TableCell>
                            )}
                            {this.isColumnVisible("accountStatus") && (
                            <TableCell align="center" style={{ fontSize: 13 }}>{row.accountStatus}</TableCell>
                            )}
                            {this.isColumnVisible("registered") && (
                            <TableCell align="center" style={{ fontSize: 13 }}>
                              {row.registered ? "Yes" : "No"}
                            </TableCell>
                            )}
                            {this.isColumnVisible("email") && (
                            <TableCell
                              align="center"
                              style={{
                                fontSize: 13,
                                maxWidth: 80,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={row.email}
                            >
                              {row.email}
                            </TableCell>
                            )}
                            {this.isColumnVisible("phone") && (
                            <TableCell
                              align="center"
                              style={{
                                fontSize: 13,
                                maxWidth: 70,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={row.phone}
                            >
                              {row.phone}
                            </TableCell>
                            )}
                            {this.isColumnVisible("linkedFacilities") && (
                            <TableCell
                              align="center"
                              style={{
                                maxWidth: 120,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontSize: 13,
                              }}
                              title={row.linkedFacilities}
                            >
                              {row.linkedFacilities}
                            </TableCell>
                            )}
                            {this.isColumnVisible("lastLoginDate") && (
                            <TableCell
                              align="center"
                              style={{ fontSize: 13 }}
                              title={row.lastLoginDate ? moment(row.lastLoginDate).format("MM/DD/YY HH:mm") : ""}
                            >
                              {row.lastLoginDate
                                ? moment(row.lastLoginDate).format("MM/DD/YY")
                                : "Never"}
                            </TableCell>
                            )}
                            {this.isColumnVisible("lastLoginBy") && (
                            <TableCell
                              align="center"
                              style={{
                                fontSize: 13,
                                maxWidth: 80,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={row.lastLoginBy || ""}
                            >
                              {row.lastLoginBy || "--"}
                            </TableCell>
                            )}
                            {this.isColumnVisible("daysSinceLastLogin") && (
                            <TableCell align="center" style={{ fontSize: 13 }}>
                              {row.daysSinceLastLogin != null ? row.daysSinceLastLogin : "--"}
                            </TableCell>
                            )}
                            {this.isColumnVisible("totalReferrals") && (
                            <TableCell align="center" style={{ fontSize: 13 }}>{row.totalReferrals || 0}</TableCell>
                            )}
                            {this.isColumnVisible("acknowledged") && (
                            <TableCell align="center" style={{ fontSize: 13 }}>{row.acknowledged || 0}</TableCell>
                            )}
                            {this.isColumnVisible("canAccept") && (
                            <TableCell align="center" style={{ fontSize: 13 }}>{row.canAccept || 0}</TableCell>
                            )}
                            {this.isColumnVisible("finalized") && (
                            <TableCell align="center" style={{ fontSize: 13 }}>{row.finalized || 0}</TableCell>
                            )}
                            {this.isColumnVisible("declined") && (
                            <TableCell align="center" style={{ fontSize: 13 }}>{row.declined || 0}</TableCell>
                            )}
                            {this.isColumnVisible("dropped") && (
                            <TableCell align="center" style={{ fontSize: 13 }}>{row.dropped || 0}</TableCell>
                            )}
                            {this.isColumnVisible("pendingAwaiting") && (
                            <TableCell align="center" style={{ fontSize: 13 }}>{row.pendingAwaiting || 0}</TableCell>
                            )}
                            {this.isColumnVisible("avgResponseTimeHours") && (
                            <TableCell align="center" style={{ fontSize: 13 }}>
                              {row.avgResponseTimeHours != null ? row.avgResponseTimeHours.toFixed(1) : "N/A"}
                            </TableCell>
                            )}
                            {this.isColumnVisible("responseRate") && (
                            <TableCell align="center" style={{ fontSize: 13 }}>
                              {row.responseRate != null ? row.responseRate.toFixed(1) + "%" : "0%"}
                            </TableCell>
                            )}
                            {this.isColumnVisible("responseRateTrend") && (
                            <TableCell align="center">
                              <span
                                style={{
                                  color: trendColors[row.responseRateTrend] || "#757575",
                                  fontWeight: "bold",
                                  fontSize: 13,
                                }}
                              >
                                {row.responseRateTrend === "Improving" && "\u2191 "}
                                {row.responseRateTrend === "Declining" && "\u2193 "}
                                {row.responseRateTrend}
                              </span>
                            </TableCell>
                            )}
                            {this.isColumnVisible("engagementStatus") && (
                            <TableCell align="center">
                              <Chip
                                label={row.engagementStatus}
                                size="small"
                                style={{
                                  fontSize: 11,
                                  ...engagementColors[row.engagementStatus] || {},
                                }}
                              />
                            </TableCell>
                            )}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={visibleHeadCells.length} align="center">
                            <div className="not-found-text">
                              {reportData
                                ? "No data available for the selected criteria."
                                : "Select date range and click Generate Report."}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                {displayData.length > 0 && (
                  <TablePagination
                    rowsPerPageOptions={PER_PAGE_OPTIONS}
                    component="div"
                    count={displayData.length}
                    rowsPerPage={perPage}
                    page={currentPage}
                    onChangePage={this.handleChangePage}
                    onChangeRowsPerPage={this.handleChangeRowsPerPage}
                  />
                )}
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}

export const ProviderEngagementReport = connect(
  mapStateToProps,
  mapDispatchToProps
)(ProviderEngagementReportClass);

export default ProviderEngagementReport;
